import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import { createQualityCase, type QualityCaseDetailResponse } from "./casesApi";
import { QualityIncidentPicker } from "./QualityIncidentPicker";
import { QualityOwnerSelect } from "./QualityOwnerSelect";
import { QualityProductFamilyPicker } from "./QualityProductFamilyPicker";
import { QualityPurchaseOrderPicker } from "./QualityPurchaseOrderPicker";
import { QualityRmaPicker } from "./QualityRmaPicker";
import { QualitySalesOrderPicker } from "./QualitySalesOrderPicker";

const MODAL_HELP = [
  "A quality case is the folder for a significant event: RMA, customer escalation, or a batch-wide finding.",
  "Link RMAs, QC incidents, sales orders, and purchase orders as chips. Source for a later CAR is inferred from those links (RMA, QC incident, or other when both).",
].join(" ");

export function CreateQualityCaseModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (detail: QualityCaseDetailResponse) => void;
}): JSX.Element | null {
  const [title, setTitle] = useState("");
  const [productFamilies, setProductFamilies] = useState<string[]>([]);
  const [salesOrderIds, setSalesOrderIds] = useState<number[]>([]);
  const [purchaseOrderIds, setPurchaseOrderIds] = useState<number[]>([]);
  const [rmaIds, setRmaIds] = useState<number[]>([]);
  const [qualityIncidentIds, setQualityIncidentIds] = useState<number[]>([]);
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setProductFamilies([]);
      setSalesOrderIds([]);
      setPurchaseOrderIds([]);
      setRmaIds([]);
      setQualityIncidentIds([]);
      setOwnerUserId(null);
      setErrorMessage(null);
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const createMutation = useMutation({
    mutationFn: createQualityCase,
    onSuccess: (detail) => {
      onCreated(detail);
      onClose();
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof ApiError ? error.message : "Could not create quality case.");
    },
  });

  if (!open) return null;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setErrorMessage(null);
    const trimmedTitle = title.trim();
    if (trimmedTitle === "") {
      setErrorMessage("Title is required.");
      return;
    }
    createMutation.mutate({
      title: trimmedTitle,
      productFamilies: productFamilies.length > 0 ? productFamilies : undefined,
      salesOrderIds: salesOrderIds.length > 0 ? salesOrderIds : undefined,
      purchaseOrderIds: purchaseOrderIds.length > 0 ? purchaseOrderIds : undefined,
      rmaIds: rmaIds.length > 0 ? rmaIds : undefined,
      qualityIncidentIds: qualityIncidentIds.length > 0 ? qualityIncidentIds : undefined,
      ownerUserId: ownerUserId ?? undefined,
    });
  };

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="create-quality-case-title">
      <form
        className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="create-quality-case-title" className="text-lg font-semibold text-slate-900">
              New quality case
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <div>
            <Label htmlFor="quality-case-title">Title</Label>
            <Input
              id="quality-case-title"
              value={title}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Short summary of the event"
            />
          </div>
          <div>
            <Label htmlFor="quality-case-incidents">QC incidents (optional)</Label>
            <QualityIncidentPicker
              id="quality-case-incidents"
              values={qualityIncidentIds}
              onChange={setQualityIncidentIds}
            />
          </div>
          <div>
            <Label htmlFor="quality-case-rmas">RMAs (optional)</Label>
            <QualityRmaPicker id="quality-case-rmas" values={rmaIds} onChange={setRmaIds} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="quality-case-sales-order">Sales orders (optional)</Label>
              <QualitySalesOrderPicker
                id="quality-case-sales-order"
                values={salesOrderIds}
                onChange={setSalesOrderIds}
              />
            </div>
            <div>
              <Label htmlFor="quality-case-purchase-order">Purchase orders (optional)</Label>
              <QualityPurchaseOrderPicker
                id="quality-case-purchase-order"
                values={purchaseOrderIds}
                onChange={setPurchaseOrderIds}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="quality-case-owner">Owner (optional)</Label>
            <QualityOwnerSelect
              id="quality-case-owner"
              value={ownerUserId}
              onChange={setOwnerUserId}
            />
          </div>
          <div>
            <Label htmlFor="quality-case-product-family">Product families (optional)</Label>
            <QualityProductFamilyPicker
              id="quality-case-product-family"
              values={productFamilies}
              onChange={setProductFamilies}
            />
          </div>
          {errorMessage ? <p className="text-sm text-rose-700">{errorMessage}</p> : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            Create case
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
